<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { dataManager } from '@/dataManager'
import type { Questionnaire } from '@/dataManager'
import { Title } from 'chart.js';

const emit = defineEmits<{
  (e: 'backButton', backUrl: string|null): void
}>()

const router = useRouter()
const dm = dataManager.getInstance()
const questionnaireList = ref<Questionnaire[]>()

onMounted(() => {
  emit('backButton', '/')
  questionnaireList.value = dm.getQuestionnaireList()
})

function goToQuestionnaire(questionnaireId: number) {
  router.push(`/questionnaire/${questionnaireId}`)
}

type QuestionnaireCard = {
  title: string
  image: string
  daysSinceDone: number|null
  target: {
      id: number
      title: string
  }[]
};

function getQuestionnaireCards(): QuestionnaireCard[] {
  let questionnaires = dm.getPendingQuestionnaires()
  return prepareQuestionnaireCards(questionnaires)
}

function getDoneQuestionnaireCards(): QuestionnaireCard[] {
  let questionnaires = dm.getDoneQuestionnaires()
  return prepareQuestionnaireCards(questionnaires)
}

function prepareQuestionnaireCards(questionnaires: Questionnaire[]): QuestionnaireCard[] {
  let cards : QuestionnaireCard[] = []
  for (let i = 0; i < questionnaires.length; i++) {
    let questionnaire = questionnaires[i];
    let [mainTitle, subQuestionnaireTitle] = dm.extractTitles(questionnaire.title)
    let targets: {id: number, title: string}[] = []
    dm.getSubQuestionnaireList(questionnaire.id).forEach((subQuestionnaire) => {
      let [mainTitle, subQuestionnaireTitle] = dm.extractTitles(subQuestionnaire.title)
      targets.push({
        id: subQuestionnaire.id,
        title: subQuestionnaireTitle
      })
    })

    let card : QuestionnaireCard = {
      title: mainTitle,
      image: questionnaire.questions[0].image,
      target: targets,
      daysSinceDone: dm.getLastTimeDays(questionnaire.id)
    }
    cards.push(card)
  }
  return cards
}

const selectorTargets = ref()

function goToQuestionnaireCard(card: QuestionnaireCard): void {
  if (card.target.length === 1) {
    goToQuestionnaire(card.target[0].id)
  } else {
    selectorTargets.value = card.target;
    (document.getElementById('target-selector') as HTMLDialogElement).showModal()
  }
}
</script>

<template>
<div>
  <h2>¡Escoge un cuestionario para comenzar!</h2>

  <p v-if="getQuestionnaireCards().length == 0" >
    ¡Llevas todos tus cuestionarios al día! Pero puedes repetir alguno si quieres.
  </p>
  <p v-else class="questionnaire-message">
    Intenta cubrir cada cuestionario cada cierto tiempo. Los siguientes cuestionarios
    no los has cubierto en los últimos 3 meses:
  </p>

  <div v-if="getQuestionnaireCards().length > 0" class="questionnaire-grid">
    <div class="questionnaire-card"
      v-for="questionnaireCard in getQuestionnaireCards()"
      @click="goToQuestionnaireCard(questionnaireCard)"
      >
      <img :src="'/images/' + questionnaireCard.image" />
      <p>{{ questionnaireCard.title }}</p>
      <p v-if="questionnaireCard.daysSinceDone != null" class="time-done">Cubierto hace {{ questionnaireCard.daysSinceDone }} días</p>
    </div>
  </div>

  <div v-if="getDoneQuestionnaireCards().length > 0">
  <h3>Cuestionarios ya cubiertos</h3>
  <div class="questionnaire-grid">
    <div class="questionnaire-card"
      v-for="questionnaireCard in getDoneQuestionnaireCards()"
      @click="goToQuestionnaireCard(questionnaireCard)"
      >
      <img :src="'/images/' + questionnaireCard.image" />
      <p>{{ questionnaireCard.title }}</p>
      <p v-if="questionnaireCard.daysSinceDone != null" class="time-done">Cubierto hace {{ questionnaireCard.daysSinceDone }} días</p>
    </div>
  </div>
  </div>

  <dialog id="target-selector" closedby="any">
    <div class="container">
      <button v-for="target in selectorTargets"
        @click="goToQuestionnaire(target.id)">{{ target.title }}</button>
    </div>
  </dialog>
</div>
</template>

<style scoped>
p {
  text-align: center;
}

.questionnaire-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 15px;


  margin-bottom: 20px;
}

.questionnaire-card {
  width: 250px;
  height: 200px;
  background-color: var(--main-color);
  color: white;
  border-radius: 4px;

  display: flex;
  flex-direction: column;

  cursor: pointer;
}

.questionnaire-card img {
  width: 250px;
  height: 150px;
  object-fit: cover;
  object-position: center center;
  padding: 4px;
  border-radius: 8px;
}

.questionnaire-card p {
  flex-grow: 1;
  align-content: center;
  margin: 0;
}

.questionnaire-card:hover {
    filter: brightness(125%);
    box-shadow: 7px 6px 28px 1px rgba(0, 0, 0, 0.24);
}

.questionnaire-card:active {
    transform: scale(0.98);
    box-shadow: 3px 2px 22px 1px rgba(0, 0, 0, 0.24);
}

.time-done {
  color: #eee;
  margin-top: -10px;
  font-style: italic;
  font-size:smaller;
}

h2 {
  margin: 10px;
}
h3 {
  margin-bottom: 15px;
}
/* Smaller */
@media (max-width: 525px) {
  .questionnaire-grid {
    gap: 5px;
  }
  .questionnaire-card {
    width: 160px;
    height: 140px;

    font-size: 8pt;
  }

  .questionnaire-card img {
    width: 160px;
    height: 105px;
  }
}

.questionnaire-message {
  margin-bottom: 15px;
}

/* Smaller, option 2 */
/*@media (max-width: 540px) {
  .questionnaire-card {
    width: 100%;
    height: 60px;
    flex-direction: row;
  }

  .questionnaire-card img {
    width: 90px;
    height: 60px;
  }
}*/
</style>
